import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import {
  X, Upload, FileUp, Type, Loader2, ArrowRight, Info, AlertTriangle, Target
} from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// File validation
const ACCEPTED_FILE_TYPES = {
  'application/pdf': ['.pdf'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'text/plain': ['.txt'],
  'text/markdown': ['.md'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
};
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

/**
 * QuickScopeModal - Quick scope upload from any location
 * Opens a modal for uploading scope document or pasting text
 * After upload, redirects to Scope Workspace with project preselected
 */
const QuickScopeModal = ({ 
  isOpen, 
  onClose, 
  projectId, 
  projectName,
  hasExistingBaseline = false 
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [uploadMode, setUploadMode] = useState('file');
  const [pasteText, setPasteText] = useState('');
  const [processing, setProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);

  const tenantId = user?.tenant?.id || user?.tenant_id;

  // Dropzone configuration
  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      const errors = rejectedFiles.map(f => {
        if (f.errors[0]?.code === 'file-too-large') {
          return `${f.file.name}: File too large (max 10MB)`;
        }
        if (f.errors[0]?.code === 'file-invalid-type') {
          return `${f.file.name}: Invalid file type`;
        }
        return `${f.file.name}: ${f.errors[0]?.message}`;
      });
      setError(errors.join(', '));
      return;
    }

    if (acceptedFiles.length > 0) {
      handleFileUpload(acceptedFiles[0]);
    }
  }, [projectId, user, tenantId]);

  const { getRootProps, getInputProps, isDragActive, acceptedFiles } = useDropzone({
    onDrop,
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_FILE_SIZE,
    multiple: false,
  });

  // File upload handler
  const handleFileUpload = async (file) => {
    if (!projectId || !user) return;

    setProcessing(true);
    setError(null);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('tenant_id', tenantId);
      formData.append('project_id', projectId);
      formData.append('user_id', user.id);
      formData.append('user_name', user.name || user.email || 'User');
      formData.append('file', file);

      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const res = await api.uploadScopeDocument(formData);
      
      clearInterval(progressInterval);
      setUploadProgress(100);

      toast.success('Document uploaded successfully!');
      
      // Navigate to scope workspace
      onClose();
      navigate(`/scope-workspace?project=${projectId}`);
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err.response?.data?.detail || 'Upload failed');
      toast.error('Upload failed');
    } finally {
      setProcessing(false);
      setUploadProgress(0);
    }
  };

  // Text upload handler
  const handleTextUpload = async () => {
    if (!projectId || !user || !pasteText.trim()) return;

    setProcessing(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('tenant_id', tenantId);
      formData.append('project_id', projectId);
      formData.append('user_id', user.id);
      formData.append('user_name', user.name || user.email || 'User');
      formData.append('raw_text', pasteText.trim());

      const res = await api.uploadScopeDocument(formData);
      
      toast.success('Text uploaded successfully!');
      
      // Navigate to scope workspace
      onClose();
      navigate(`/scope-workspace?project=${projectId}`);
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err.response?.data?.detail || 'Upload failed');
      toast.error('Upload failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmit = () => {
    if (uploadMode === 'file' && acceptedFiles.length > 0) {
      handleFileUpload(acceptedFiles[0]);
    } else if (uploadMode === 'paste') {
      handleTextUpload();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50" 
        onClick={onClose}
        data-testid="quick-scope-backdrop"
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="bg-white rounded-xl shadow-2xl w-full max-w-lg" 
          onClick={e => e.stopPropagation()}
          data-testid="quick-scope-modal"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[var(--clr-brand-primary)]/10 flex items-center justify-center">
                <Target className="w-5 h-5 text-[var(--clr-brand-primary)]" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Quick Scope</h3>
                <p className="text-xs text-gray-500">Upload scope for {projectName}</p>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              data-testid="close-quick-scope"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Existing baseline warning */}
          {hasExistingBaseline && (
            <div className="mx-6 mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Scope is baselined</p>
                <p className="text-amber-700 mt-0.5">
                  Uploading new scope will create a new Draft for review.
                </p>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="p-6">
            {/* Mode toggle */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setUploadMode('file')}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                  uploadMode === 'file'
                    ? 'bg-[var(--clr-brand-primary)] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid="quick-scope-mode-file"
              >
                <FileUp className="w-4 h-4" />
                Upload File
              </button>
              <button
                onClick={() => setUploadMode('paste')}
                className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                  uploadMode === 'paste'
                    ? 'bg-[var(--clr-brand-primary)] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid="quick-scope-mode-paste"
              >
                <Type className="w-4 h-4" />
                Paste Text
              </button>
            </div>

            {/* Upload area */}
            {uploadMode === 'file' ? (
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  isDragActive
                    ? 'border-[var(--clr-brand-primary)] bg-[var(--clr-brand-primary)]/5'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                data-testid="quick-scope-dropzone"
              >
                <input {...getInputProps()} data-testid="quick-scope-file-input" />
                <Upload className={`w-10 h-10 mx-auto mb-3 ${isDragActive ? 'text-[var(--clr-brand-primary)]' : 'text-gray-400'}`} />
                {acceptedFiles.length > 0 ? (
                  <div>
                    <p className="text-sm text-gray-700 font-medium">{acceptedFiles[0].name}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {(acceptedFiles[0].size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                ) : isDragActive ? (
                  <p className="text-[var(--clr-brand-primary)] font-medium">Drop the file here</p>
                ) : (
                  <>
                    <p className="text-gray-600 mb-1">Drag & drop or click to upload</p>
                    <p className="text-xs text-gray-400">.docx, .pdf, .txt, .md, .xlsx (max 10MB)</p>
                  </>
                )}
              </div>
            ) : (
              <textarea
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                placeholder="Paste your scope document text here..."
                rows={8}
                className="w-full p-3 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:outline-none"
                data-testid="quick-scope-textarea"
              />
            )}

            {/* Error display */}
            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-700">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Upload progress */}
            {processing && uploadMode === 'file' && (
              <div className="mt-4">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-600">Uploading...</span>
                  <span className="text-gray-500">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-[var(--clr-brand-primary)] h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Info */}
            <div className="flex items-center gap-2 mt-4 p-3 bg-blue-50 rounded-lg">
              <Info className="w-4 h-4 text-blue-500 flex-shrink-0" />
              <p className="text-xs text-blue-700">
                After upload, you'll be taken to the Scope Workspace to run AI extraction.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              data-testid="quick-scope-cancel"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={processing || (uploadMode === 'file' ? !acceptedFiles.length : !pasteText.trim())}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
              data-testid="quick-scope-submit"
            >
              {processing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ArrowRight className="w-4 h-4" />
              )}
              {processing ? 'Processing...' : 'Upload & Continue'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default QuickScopeModal;
